<template>
    <section name="generic-info" class="gap-xs lg:gap-md grid grid-cols-2 lg:grid-cols-4">
        <button
            v-for="item in genericData"
            :key="item.group"
            class="card gap-lg bg-primary disabled:bg-primary hack flex w-full cursor-auto flex-col disabled:opacity-100 sm:max-w-80 lg:min-w-60"
            :class="{
                'cursor-pointer hover:border-(--border-focus)': isClickable(item),
                hidden: shouldHide(item),
            }"
            :disabled="isDisabled(item)"
            @click="
                item.group === 'power' ||
                item.group === 'humidity' ||
                item.group === 'temperature' ||
                item.group === 'pue'
                    ? $router.push({ name: 'graphs' })
                    : item.group === 'sites'
                      ? $router.push({ name: 'locations' })
                      : item.group === 'users'
                        ? $router.push({ name: 'users' })
                        : item.group === 'groups'
                          ? $router.push({ name: 'groups' })
                          : null
            "
        >
            <div class="flex w-full justify-between">
                <div class="gap-sm flex items-center">
                    <p class="font-semibold first-letter:uppercase">
                        {{ item.group }}
                    </p>
                </div>

                <i v-if="item.info" class="i-info hidden h-6 w-6 lg:block!" :title="item.info" />
            </div>
            <div class="gap-xs flex w-full items-end">
                <p class="text-title-xl font-bold">{{ item.value }}</p>
                <div class="gap-xs flex">
                    <p class="mb-1 text-xl font-bold">
                        {{ item.unit ?? item.group }}
                    </p>
                </div>
                <i
                    class="ml-auto h-10 w-10"
                    :class="{
                        'i-site': item.group === 'sites',
                        'i-users': item.group === 'users',
                        'i-groups': item.group === 'groups',
                        'i-power': item.group === 'power',
                    }"
                />
            </div>
        </button>
    </section>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useOrganizationStore } from '@/stores/organization';
import { mapUpDataCards } from '@/stores/helpers';

import type { GenericInfoCard, UUIDLocation } from '@/stores/store-interfaces';

import type { PropType } from 'vue';

const organizationStore = useOrganizationStore();
const genericData = ref<Array<GenericInfoCard>>([]);
const usersOrganization = computed(() => organizationStore.usersOrganization);
const user = computed(() => organizationStore.user);
const intervalId = ref<ReturnType<typeof setInterval>>();
const cascadingLocations = ref<UUIDLocation[]>([]);

// So we don't spam error messages to users
const errorSites = ref<Set<string>>(new Set());

// Define access rules for each group
const canAccessGroup = (groupName: string) => {
    const accessRules = {
        sites: organizationStore.isOrgAdmin,
        users: organizationStore.isOrgAdmin,
        groups: organizationStore.isOrgAdmin,
        power: organizationStore.hasDataAccess,
        temperature: organizationStore.hasDataAccess,
        pue: organizationStore.hasDataAccess,
        humidity: organizationStore.hasDataAccess,
    };

    return accessRules[groupName as keyof typeof accessRules] || false;
};

const isClickable = (item: GenericInfoCard) => {
    return canAccessGroup(item.group) && props.show === 'all';
};

const isDisabled = (item: GenericInfoCard) => {
    return !canAccessGroup(item.group) || props.show !== 'all';
};

const shouldHide = (item: GenericInfoCard) => {
    return item.group === 'power' && usersOrganization.value?.organization_id === 141;
};

const props = defineProps({
    show: {
        type: String,
        default: 'all',
        required: false,
    },
    data: {
        type: Array as PropType<GenericInfoCard[]>,
        required: false,
    },
});

onMounted(async () => {
    await setup();
});

onUnmounted(() => {
    clearInterval(intervalId.value);
});

watch(usersOrganization, async () => {
    await setup();
});

watch(user, async () => {
    await setup();
});

async function setup() {
    if (usersOrganization.value === undefined || user.value === undefined) {
        return;
    }

    cascadingLocations.value = await organizationStore.getLocations();

    genericData.value = [];
    if (
        usersOrganization.value === undefined ||
        user.value === undefined ||
        (genericData.value.length > 0 && !props.data)
    ) {
        return;
    }

    if (!props.data) {
        if (props.show === 'data' || props.show === 'all') {
            await organizationStore
                .getGenericData()
                .then((response) => {
                    for (const [name, value] of Object.entries(response)) {
                        genericData.value.push({
                            sensorId: '',
                            sensorName: '',
                            time: '',
                            building: '',
                            group: name.split('_')[0],
                            value: Number(value),
                            unit: undefined,
                            info:
                                name.split('_')[0] === 'groups'
                                    ? 'Total number of groups'
                                    : name.split('_')[0] === 'users'
                                      ? 'Total number of users'
                                      : name.split('_')[0] === 'sites'
                                        ? 'Total number of sites'
                                        : undefined,
                        });
                    }
                })
                .catch((error) => {
                    console.error(error);
                });

            if (organizationStore.hasDataAccess) {
                const allLiveData = await getDataCards();

                const mappedUpDataCards = mapUpDataCards(allLiveData, [usersOrganization.value.name]);
                genericData.value.push(...mappedUpDataCards);

                beginInterval();
            }
        }
    } else {
        genericData.value = props.data;
    }
}

async function getDataCards() {
    const data: GenericInfoCard[] = [];

    // Map data roles to their corresponding data types
    const roleToDataType = {
        'stat-power': 'power',
        'stat-temperature': 'temperature',
        'stat-humidity': 'humidity',
        'stat-pue': 'pue',
    };

    const dataTypeInfo = {
        power: 'Current power consumption',
        temperature: 'Current temperature',
        humidity: 'Current humidity',
        pue: 'Current PUE',
    };

    // Get only the data types the user has access to
    const allowedDataTypes = [];

    if (!organizationStore.isOrgAdmin) {
        allowedDataTypes.push(
            ...organizationStore.currentUserRoles
                .filter((role) => role.name in roleToDataType)
                .map((role) => roleToDataType[role.name as keyof typeof roleToDataType]),
        );
    } else {
        allowedDataTypes.push(
            ...organizationStore.currentOrgRoles
                .filter((role) => role.name in roleToDataType)
                .map((role) => roleToDataType[role.name as keyof typeof roleToDataType]),
        );
    }

    // If user has no data roles, return empty array
    if (allowedDataTypes.length === 0 && !organizationStore.isOrgAdmin) {
        return data;
    }

    if (cascadingLocations.value[0]?.children?.length === 0) {
        return data;
    }
    // Fetch data only for allowed types
    await Promise.all(
        allowedDataTypes.map(async (dataType) => {
            const sites = cascadingLocations.value.flatMap(
                (location) => location.children?.map((child) => child.name) || [],
            );

            // Skip sites we already know are problematic
            const validSites = sites.filter((s) => !errorSites.value.has(s));
            await Promise.all(
                validSites.map(async (site) => {
                    try {
                        const response = await organizationStore.getLiveData(dataType, site, site);
                        if (response) {
                            response.forEach((item) => {
                                data.push({
                                    sensorId: item.sensorId,
                                    sensorName: item.sensorName,
                                    time: item.time,
                                    building: item.building,
                                    group: item.group,
                                    value: item.value,
                                    info: dataTypeInfo[dataType as keyof typeof dataTypeInfo],
                                });
                            });
                        }
                    } catch (error) {
                        console.error(`Error fetching ${dataType} data for ${site}:`, error);
                        // Add the failing site to our skip list
                        errorSites.value.add(site);
                    }
                }),
            );
        }),
    );

    return data;
}

function beginInterval() {
    intervalId.value = setInterval(async () => {
        const newData = await getDataCards();
        const mappedUpDataCards = mapUpDataCards(newData, [usersOrganization.value?.name ?? '']);

        genericData.value.forEach((item) => {
            const updatedItem = mappedUpDataCards.find((newItem) => newItem.group === item.group);
            if (updatedItem) {
                item.value = updatedItem.value;
            }
        });
    }, 10000);
}

watch(
    () => props.data,
    (newVal) => {
        if (newVal !== undefined) {
            // if props.data is already mapped, just assign it
            genericData.value = props.show === 'data' ? newVal : newVal;
        }
    },
);
</script>

<style>
.hack:disabled i {
    opacity: 1;
}
</style>
